import React from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { AH_BORDER, AH_INPUT_BORDER, AH_MUTED, AH_SUBTLE, AH_TEXT, AH_TRACK, AH_WHITE } from "../../AuditHub/theme";

export const TASK_STATUS = {
  OPEN: { label: "Open", color: "#c2410c", tint: "#fff7ed", border: "#fed7aa" },
  DONE: { label: "Done", color: "#16a34a", tint: "#f0fdf4", border: "#bbf7d0" },
  CANCELLED: { label: "Cancelled", color: "#6b7280", tint: "#f9fafb", border: "#e5e7eb" },
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AH_MUTED,
};

const cardSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "10px",
  px: 1.5,
  py: 1.25,
  backgroundColor: AH_WHITE,
};

const iconTileSx = {
  width: 32,
  height: 32,
  borderRadius: "8px",
  backgroundColor: AH_TRACK,
  color: AH_SUBTLE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const actionSx = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: `1px solid ${AH_INPUT_BORDER}`,
  backgroundColor: AH_WHITE,
  color: AH_SUBTLE,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#334155" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9", backgroundColor: "#f8fafc" },
  "& .MuiSvgIcon-root": { fontSize: 16 },
};

const doneActionSx = {
  ...actionSx,
  color: "#16a34a",
  borderColor: "#bbf7d0",
  backgroundColor: "#f0fdf4",
  "&:hover": { backgroundColor: "#dcfce7", color: "#15803d" },
};

const cancelActionSx = {
  ...actionSx,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
};

const newTaskButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "8px",
  height: 34,
  minHeight: 34,
  px: 1.5,
  fontSize: "0.8125rem",
  boxShadow: "none",
  color: "#1976d2",
  borderColor: "#bfdbfe",
  backgroundColor: AH_WHITE,
  "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 17 },
  "&:hover": { borderColor: "#1976d2", backgroundColor: "#eff6ff", boxShadow: "none" },
  "&.Mui-disabled": { color: "#94a3b8", borderColor: AH_BORDER },
};

const statusChipSx = (status) => ({
  display: "inline-flex",
  alignItems: "center",
  height: 20,
  px: 0.875,
  borderRadius: "999px",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: "nowrap",
  color: status.color,
  backgroundColor: status.tint,
  border: `1px solid ${status.border}`,
});

const toLocalDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatDay = (value) => {
  const date = toLocalDate(value);
  return date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

const isOverdue = (value) => {
  const date = toLocalDate(value);
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const ControlTasks = ({ tasks, openTaskCount, locked, disabled, statusLocked = false, nameFor, formatWhen, onNewTask, onSetStatus, footer }) => {
  const list = Array.isArray(tasks) ? tasks : [];
  const openCount = Number.isFinite(openTaskCount)
    ? openTaskCount
    : list.filter((task) => task.status === "OPEN").length;
  const inactive = locked || disabled;
  const statusInactive = inactive || statusLocked;
  const statusHint = statusLocked && !inactive
    ? "Only the assignee, the assessment owner, or an auditor of this assessment can change the status."
    : "";

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.25 }}>
        <Typography sx={sectionLabelSx}>Tasks</Typography>
        {list.length ? (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_SUBTLE }}>{openCount} open</Typography>
        ) : null}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
        {list.map((task) => {
          const status = TASK_STATUS[task.status] || { label: task.status || "—", ...TASK_STATUS.CANCELLED };
          const isOpen = task.status === "OPEN";
          const overdue = isOpen && isOverdue(task.dueDate);
          const meta = [
            task.assigneeEmail ? `Assigned to ${nameFor(task.assigneeEmail)}` : "",
            task.createdBy ? `Created by ${nameFor(task.createdBy)}` : "",
            formatWhen(task.createdAt),
          ].filter(Boolean);
          const completed =
            task.status === "DONE" && task.completedBy
              ? [`Completed by ${nameFor(task.completedBy)}`, formatWhen(task.completedAt)].filter(Boolean).join(" · ")
              : "";
          return (
            <Box key={task.taskId} sx={cardSx}>
              <Box sx={iconTileSx}>
                <AssignmentOutlinedIcon />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: task.status === "CANCELLED" ? AH_MUTED : AH_TEXT,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Box component="span" sx={statusChipSx(status)}>
                    {status.label}
                  </Box>
                </Box>
                {task.description ? (
                  <Typography sx={{ fontSize: 12.5, color: "#374151", mt: 0.25, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {task.description}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25, overflowWrap: "anywhere" }}>
                  {meta.join(" · ")}
                  {task.dueDate ? (
                    <>
                      {meta.length ? " · " : ""}
                      <Box component="span" sx={overdue ? { color: "#dc2626", fontWeight: 600 } : undefined}>
                        {overdue ? "Overdue" : "Due"} {formatDay(task.dueDate)}
                      </Box>
                    </>
                  ) : null}
                </Typography>
                {completed ? <Typography sx={{ fontSize: 12, color: AH_MUTED }}>{completed}</Typography> : null}
              </Box>
              <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
                {isOpen ? (
                  <>
                    <Tooltip title={statusHint || "Mark done"}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Mark task ${task.title} done`}
                          disabled={statusInactive}
                          onClick={() => onSetStatus(task, "DONE")}
                          sx={doneActionSx}
                        >
                          <CheckRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={statusHint || "Cancel task"}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Cancel task ${task.title}`}
                          disabled={statusInactive}
                          onClick={() => onSetStatus(task, "CANCELLED")}
                          sx={cancelActionSx}
                        >
                          <BlockRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title={statusHint || "Reopen"}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Reopen task ${task.title}`}
                        disabled={statusInactive}
                        onClick={() => onSetStatus(task, "OPEN")}
                        sx={actionSx}
                      >
                        <ReplayRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
        {!list.length ? <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No tasks yet.</Typography> : null}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Button variant="outlined" disabled={inactive} onClick={onNewTask} startIcon={<AddRoundedIcon />} sx={newTaskButtonSx}>
          New Task
        </Button>
        {footer}
      </Box>
    </>
  );
};

export default ControlTasks;
